import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Cancel, CheckCircle, Close } from '@mui/icons-material';
import { Autocomplete, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { BrandProfileField, BrandProfileFieldName, BrandProfilePayload } from 'domains/brand/types';
import { ContentTypeForComparison } from 'domains/content/types';
import Button from 'domains/core/components/Button';
import { greyChipStyles } from 'domains/core/components/Chip/Chip';
import PageLoader from 'domains/core/components/PageLoader';
import { SettingsSectionHeaderIcon } from 'domains/core/components/SettingsHeader';
import SettingsPageLayout from 'domains/core/components/SettingsPageLayout';
import TextField from 'domains/core/components/TextField';
import useUpdateBrandProfile from 'hooks/mutations/useUpdateBrandProfile';
import useBrandProfile from 'hooks/queries/useBrandProfile';
import useContentWithFolders from 'hooks/queries/useContentWithFolders';
import { URLPaths } from 'models/enums';

import BrandLearningStrengthCard from './BrandLearningStrengthCard';
import { BRAND_PROFILE_FIELD_DISPLAY_ORDER, isFieldSatisfied } from './brandLearningStrengthUtils';
import BrandSetupErrorPage from './BrandSetupErrorPage';

const FIELD_LABELS: Record<BrandProfileFieldName, string> = {
    [BrandProfileFieldName.OrganizationName]: 'Organization name',
    [BrandProfileFieldName.BrandDescription]: 'Describe your brand voice',
    [BrandProfileFieldName.BrandToneKeywords]: 'Brand tone keywords',
    [BrandProfileFieldName.UseKeywords]: 'Words and phrases to use',
    [BrandProfileFieldName.DontUseKeywords]: 'Words and phrases to avoid',
    [BrandProfileFieldName.SampleTemplateIds]: 'Sample content',
};

const FIELD_TOOLTIPS: Record<BrandProfileFieldName, string> = {
    [BrandProfileFieldName.OrganizationName]:
        'Helps AI reference your organization correctly in messages. Check capitalization and spacing.',
    [BrandProfileFieldName.BrandDescription]:
        'Describe how your organization speaks with patients. Include tone, values, and communication style. Minimum 50 words.',
    [BrandProfileFieldName.BrandToneKeywords]: 'Add keywords that define your brand voice.',
    [BrandProfileFieldName.UseKeywords]: 'Add terms your organization prefers using in communication.',
    [BrandProfileFieldName.DontUseKeywords]: 'Add words or phrases your brand prefers not to use.',
    [BrandProfileFieldName.SampleTemplateIds]:
        'Provide real content (emails or texts) that reflect your brand voice. Provide 2-4 examples.',
};

type BrandProfileFieldLabelProps = {
    htmlFor?: string;
    isRequired: boolean;
    label: string;
    startAdornment?: ReactNode;
};

/** Required marker: small red dot (Settings / brand setup design). */
const BrandProfileFieldLabel = ({ htmlFor, isRequired, label, startAdornment }: BrandProfileFieldLabelProps) => (
    <Stack
        alignItems="center"
        component={htmlFor ? 'label' : 'div'}
        direction="row"
        gap={0.75}
        htmlFor={htmlFor}
        sx={{ cursor: htmlFor ? 'pointer' : undefined, width: 'fit-content' }}
    >
        {startAdornment ? (
            <Box alignItems="center" component="span" display="flex" sx={{ flexShrink: 0 }}>
                {startAdornment}
            </Box>
        ) : null}
        <Typography component="span" color="text.primary" fontSize={18} variant="subtitle2">
            {label}
        </Typography>
        {isRequired ? (
            <Box
                aria-hidden
                component="span"
                data-testid="brand-profile-required-dot"
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    flexShrink: 0,
                }}
            />
        ) : null}
    </Stack>
);

const getInitialPayload = (brandProfile: BrandProfileField[]): BrandProfilePayload => {
    const payload: BrandProfilePayload = {};
    brandProfile.forEach((field) => {
        payload[field.fieldName] = field.currentValue ?? (field.inputType === 'text' ? '' : []);
    });
    return payload;
};

/** True when the API had no stored value for this field (null, '', or empty array). */
const isStoredProfileValueEmpty = (field: BrandProfileField): boolean => {
    const v = field.currentValue;
    if (v === null || v === undefined) return true;
    if (typeof v === 'string' && v === '') return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
};

const BrandSetupEditPage = () => {
    const history = useHistory();
    const { data: brandProfile = [], isLoading, isError, refetch } = useBrandProfile();
    const { mutate: updateBrandProfile, isLoading: isSaving } = useUpdateBrandProfile();
    const { data: contentData = [] } = useContentWithFolders({
        folderId: -1,
        statuses: ['published', 'live'],
    });
    const contentItems = contentData.filter((item: { type?: string }) => item.type !== ContentTypeForComparison.FOLDER);

    const [formState, setFormState] = useState<BrandProfilePayload>({});

    useEffect(() => {
        if (brandProfile.length > 0 && Object.keys(formState).length === 0) {
            setFormState(getInitialPayload(brandProfile));
        }
    }, [brandProfile, formState]);

    const handleCancel = () => {
        history.push(URLPaths.SETTINGS_BRAND);
    };

    const handleSave = () => {
        updateBrandProfile(formState, {
            onSuccess: () => {
                history.push(URLPaths.SETTINGS_BRAND);
            },
        });
    };

    const updateField = (fieldName: string, value: string | string[] | number[] | null) => {
        setFormState((prev) => ({ ...prev, [fieldName]: value }));
    };

    const isFormValid = useMemo(
        () =>
            brandProfile
                .filter((f) => f.isRequired)
                .every((f) => isFieldSatisfied(formState[f.fieldName], f.fieldName)),
        [brandProfile, formState]
    );

    const hadAnyOriginalBrandValuesOnLoad = useMemo(() => brandProfile.some((f) => !isStoredProfileValueEmpty(f)), [
        brandProfile,
    ]);
    const saveButtonLabel = hadAnyOriginalBrandValuesOnLoad ? 'Save changes' : 'Save';

    const isSaveDisabled = !isFormValid || isSaving;
    const saveDisabledTooltipTitle = !isFormValid ? 'all required fields should be filled.' : '';

    if (isLoading) {
        return (
            <Stack data-testid="brand-setup-edit-page">
                <PageLoader />
            </Stack>
        );
    }
    if (isError) {
        return <BrandSetupErrorPage onRetry={refetch} />;
    }

    return (
        <Stack data-testid="brand-setup-edit-page" role="main" aria-label="Brand Setup - Edit">
            <SettingsPageLayout
                header="brand setup"
                headerIcon={<SettingsSectionHeaderIcon />}
                headerAction={
                    <Stack direction="row" spacing={2}>
                        <Button variant="text" onClick={handleCancel} data-testid="brand-setup-cancel-button">
                            Cancel
                        </Button>
                        <Tooltip
                            disableFocusListener={isFormValid}
                            disableHoverListener={isFormValid}
                            disableTouchListener={isFormValid}
                            title={saveDisabledTooltipTitle}
                        >
                            <Box
                                component="span"
                                data-testid="brand-setup-save-button-tooltip-anchor"
                                sx={{ display: 'inline-block' }}
                            >
                                <Button
                                    variant="contained"
                                    disabled={isSaveDisabled}
                                    data-testid="brand-setup-save-button"
                                    onClick={handleSave}
                                >
                                    {saveButtonLabel}
                                </Button>
                            </Box>
                        </Tooltip>
                    </Stack>
                }
            >
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={4}
                    alignItems="flex-start"
                    justifyContent="space-between"
                >
                    <Stack spacing={3} flex={1} maxWidth={600}>
                        <Stack spacing={1} flex={1} maxWidth={600}>
                            <Typography variant="h6" fontWeight={500} color="text.primary">
                                set up your brand&apos;s voice and tone
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">
                                Help AI assistant understand how your organization communicates with its audience.
                            </Typography>
                        </Stack>
                        {[...brandProfile]
                            .sort(
                                (a, b) =>
                                    (BRAND_PROFILE_FIELD_DISPLAY_ORDER.indexOf(a.fieldName as BrandProfileFieldName) ??
                                        999) -
                                    (BRAND_PROFILE_FIELD_DISPLAY_ORDER.indexOf(b.fieldName as BrandProfileFieldName) ??
                                        999)
                            )
                            .map((field) => (
                                <BrandProfileFieldInput
                                    key={field.fieldName}
                                    field={field}
                                    value={formState[field.fieldName]}
                                    contentItems={contentItems}
                                    onChange={(value) => updateField(field.fieldName, value)}
                                />
                            ))}
                    </Stack>
                    <BrandLearningStrengthCard mode="edit" brandProfile={brandProfile} formValues={formState} />
                </Stack>
            </SettingsPageLayout>
        </Stack>
    );
};

type ContentItem = { id: number; name: string; type?: string };

type BrandProfileFieldInputProps = {
    field: BrandProfileField;
    value: string | string[] | number[] | null | undefined;
    contentItems: ContentItem[];
    onChange: (value: string | string[] | number[] | null) => void;
};

const getWordCount = (str: string): number => (str.trim() ? str.trim().split(/\s+/).filter(Boolean).length : 0);

const BrandProfileFieldInput = ({ field, value, contentItems, onChange }: BrandProfileFieldInputProps) => {
    const label = FIELD_LABELS[field.fieldName as BrandProfileFieldName] ?? field.fieldName;
    const tooltip = FIELD_TOOLTIPS[field.fieldName as BrandProfileFieldName];
    const strValue = field.inputType === 'text' ? (value as string) ?? '' : '';
    const wordCount = field.inputType === 'text' ? getWordCount(strValue) : 0;
    const [touched, setTouched] = useState(false);

    if (field.inputType === 'text') {
        const isDescription = field.fieldName === BrandProfileFieldName.BrandDescription;
        const brandDescriptionHelperText = isDescription
            ? wordCount < 50
                ? `Minimum 50 words required. ${wordCount} / 400`
                : `${wordCount} / 400`
            : undefined;
        const hasBrandDescriptionError = isDescription && touched && wordCount < 50;
        const textFieldId = `brand-setup-field-${field.fieldName}`;
        const textFieldBlock = (
            <Stack spacing={1}>
                <BrandProfileFieldLabel htmlFor={textFieldId} isRequired={field.isRequired} label={label} />
                <TextField
                    id={textFieldId}
                    value={strValue}
                    onBlur={() => setTouched(true)}
                    onChange={(e) => {
                        setTouched(true);
                        onChange(e.target.value);
                    }}
                    required={field.isRequired}
                    multiline={isDescription}
                    rows={isDescription ? 4 : 1}
                    placeholder={
                        isDescription
                            ? "e.g. Our brand's voice is polite, respectful, and reassuring. We prioritize patients in every interaction and strive to communicate with clarity and empathy."
                            : field.fieldName === BrandProfileFieldName.OrganizationName
                            ? 'e.g. Socratic Health'
                            : undefined
                    }
                    withCounter={isDescription}
                    counterCurrent={wordCount}
                    counterMax={isDescription ? 4000 : 100}
                    helperText={brandDescriptionHelperText}
                    error={hasBrandDescriptionError}
                    fullWidth
                    sx={{ '& .MuiFilledInput-input': { maxHeight: isDescription ? 120 : undefined } }}
                />
            </Stack>
        );
        return tooltip ? <Tooltip title={tooltip}>{textFieldBlock}</Tooltip> : textFieldBlock;
    }

    if (field.inputType === 'text[]') {
        const arrValue = (Array.isArray(value) ? value : []) as string[];
        const placeholder =
            field.fieldName === BrandProfileFieldName.BrandToneKeywords
                ? 'e.g. Empathetic, Professional, Friendly'
                : field.fieldName === BrandProfileFieldName.UseKeywords
                ? 'e.g. Patient first, personalized care'
                : "e.g. don't wait, guaranteed results";
        const useOrAvoidLabelAdornment =
            field.fieldName === BrandProfileFieldName.UseKeywords ? (
                <CheckCircle
                    aria-hidden
                    data-testid="brand-profile-use-keywords-label-icon"
                    sx={{ fontSize: 20 }}
                    color="success"
                />
            ) : field.fieldName === BrandProfileFieldName.DontUseKeywords ? (
                <Cancel
                    aria-hidden
                    data-testid="brand-profile-dont-use-keywords-label-icon"
                    sx={{ fontSize: 20 }}
                    color="error"
                />
            ) : undefined;
        const textArrayField = (
            <Stack spacing={1}>
                <BrandProfileFieldLabel
                    isRequired={field.isRequired}
                    label={label}
                    startAdornment={useOrAvoidLabelAdornment}
                />
                <Autocomplete
                    multiple
                    freeSolo
                    options={field.allowedValues ?? []}
                    value={arrValue}
                    onChange={(_e, newValue) => onChange(newValue)}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip
                                key={option}
                                deleteIcon={<Close />}
                                label={option}
                                {...getTagProps({ index })}
                                sx={greyChipStyles}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={placeholder}
                            helperText={
                                field.fieldName === BrandProfileFieldName.BrandToneKeywords
                                    ? 'Press enter to add a keyword'
                                    : undefined
                            }
                        />
                    )}
                    sx={{ width: '100%' }}
                />
            </Stack>
        );
        return tooltip ? <Tooltip title={tooltip}>{textArrayField}</Tooltip> : textArrayField;
    }

    if (field.inputType === 'integer[]' && field.fieldName === BrandProfileFieldName.SampleTemplateIds) {
        const MAX_SAMPLE_TEMPLATES = 4;
        const arrValue = (Array.isArray(value) ? value : []) as number[];
        const selectedContent = arrValue
            .map((id) => contentItems.find((c) => c.id === id))
            .filter(Boolean) as ContentItem[];
        const sampleContentField = (
            <Stack spacing={1}>
                <BrandProfileFieldLabel isRequired={field.isRequired} label={label} />
                <Autocomplete
                    multiple
                    options={contentItems}
                    getOptionLabel={(opt) => (typeof opt === 'object' ? opt.name : '')}
                    value={selectedContent}
                    onChange={(_e, newValue) => onChange(newValue.slice(0, MAX_SAMPLE_TEMPLATES).map((c) => c.id))}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip
                                key={option.id}
                                deleteIcon={<Close />}
                                label={option.name}
                                {...getTagProps({ index })}
                                sx={greyChipStyles}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select sample content"
                            helperText={`Maximum ${MAX_SAMPLE_TEMPLATES} templates`}
                        />
                    )}
                    sx={{ width: '100%' }}
                />
            </Stack>
        );
        return tooltip ? <Tooltip title={tooltip}>{sampleContentField}</Tooltip> : sampleContentField;
    }

    return null;
};

export default BrandSetupEditPage;
